import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import Tooltip from '@material-ui/core/Tooltip';
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom';
import { PROFILES } from '../../../utils/constants';
import Button from '@mui/material/Button';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { toast } from 'react-toastify';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ClearIcon from '@mui/icons-material/Clear';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import AppBar from '@mui/material/AppBar';
import Container from "../../GeneralComponents/Container";
import { Topbar, Sidebar, Table } from '../components';
import { deleteExpense, getExpenses, getExpenseSummary, getOrganizations } from '../../../redux/actions/apiActions'
import { EXPENSE_TYPES } from './AddExpense';
import setGlobalLoader from '../../../redux/actions/loaderActions'
import exportToExcel from '../../../utils/exportToExcel';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

const getExpenseLabel = (type) =>
  EXPENSE_TYPES.find((t) => t.value === type)?.label || type;

const TableComponent = React.forwardRef(({ columns, data, title, isRemoteData }, ref) => {
  const theme = useTheme();

  return (
    <Box p={4}>
      <Box
        width={1}
        height={1}
        borderRadius={2}
        border={`2px solid ${theme.palette.divider}`}
        sx={{
          borderStyle: 'dashed',
        }}
      >
        <Table
          ref={ref}
          columns={columns}
          data={data}
          export={false}
          selection={false}
          toolbar
          title={title}
          isRemoteData={isRemoteData}
        />
      </Box>
    </Box>
  );
});

const Expenses = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const profile = useSelector(state => state.authReducer.profile);
  const tableRef = useRef(null);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [selectedExpenseType, setSelectedExpenseType] = useState(null);
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });

  // Date filter state
  const today = new Date();
  const minDateObj = new Date('2025-01-01');

  // Calculate default dates based on profile
  const getDefaultStartDate = () => {
    if (profile === PROFILES.ADMIN) {
      return new Date(today.getFullYear(), today.getMonth(), 1);
    }
    return null;
  };

  const getDefaultEndDate = () => {
    if (profile === PROFILES.ADMIN) {
      return today;
    }
    return null;
  };

  const [dateRange, setDateRange] = useState([
    {
      startDate: getDefaultStartDate(),
      endDate: getDefaultEndDate(),
      key: 'selection',
    },
  ]);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Getter functions for startDate and endDate
  const startDate = dateRange[0].startDate;
  const endDate = dateRange[0].endDate;

  // Handle date range change
  const handleDateRangeChange = (item) => {
    setDateRange([item.selection]);
  };

  const [summary, setSummary] = useState(null);
  const [openSidebar, setOpenSidebar] = useState(false);

  const handleSidebarOpen = () => {
    setOpenSidebar(true);
  };

  const handleSidebarClose = () => {
    setOpenSidebar(false);
  };
  const open = isMd ? false : openSidebar;

  const handleDeleteExpense = async (rowData) => {
    let text = `Are you sure you want to delete this expense?`;
    if (window.confirm(text) === true) {
      dispatch(setGlobalLoader(true))
      try {
        await deleteExpense(rowData._id);
        dispatch(setGlobalLoader(false))
        toast.success('Expense deleted successfully');
        if (tableRef.current) {
          tableRef.current.onQueryChange();
        }
      } catch (error) {
        dispatch(setGlobalLoader(false))
        console.log("delete expense error", error);
        toast.error(error?.response?.data?.msg ?? 'Error deleting expense');
      }
    } else {
      toast.info('Request Cancelled');
    }
  }

  const columns = [
    { title: 'Sr no.', field: 'index', filtering: false },
    { title: 'Amount', field: 'amount', filtering: false },
    { title: 'Category', field: 'type', filtering: false },
    { title: 'Description', field: 'description', filtering: false },
    { title: 'Date', field: 'date', filtering: false },
    ...(profile === PROFILES.ADMIN ? [{ title: 'Organization', field: 'organizationName', filtering: false }] : []),
    {
      title: 'Actions',
      field: '',
      filtering: false,
      render: (rowData) => (
        <div className="tableActions ">
          <Tooltip title="Edit">
            <EditIcon onClick={() =>
              navigate(`/expenses/edit/${rowData._id}`, {
                state: {
                  expenseData: rowData
                }
              })} />
          </Tooltip>
          <Tooltip title="Delete">
            <DeleteIcon
              onClick={() => handleDeleteExpense(rowData)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  // Server-side pagination fetch for material-table
  const fetchExpenses = async (query) => {
    return new Promise(async (resolve) => {
      try {
        dispatch(setGlobalLoader(true));

        const page = query.page + 1;
        const limit = query.pageSize;

        const queryParams = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });

        if (selectedOrganization?.value) {
          queryParams.append('organizationId', selectedOrganization.value);
        }
        if (startDate) {
          queryParams.append('startDate', format(startDate, 'yyyy-MM-dd'));
        }
        if (endDate) {
          queryParams.append('endDate', format(endDate, 'yyyy-MM-dd'));
        }
        if (selectedExpenseType?.value) {
          queryParams.append('type', selectedExpenseType.value);
        }

        const expenseRes = await getExpenses(`?${queryParams.toString()}`);
        const apiData = expenseRes?.data?.data;

        const data = (apiData?.docs || []).map((e, i) => ({
          ...e,
          index: (page - 1) * limit + i + 1,
          type: getExpenseLabel(e?.type),
          date: e?.createdAt ? format(new Date(e.createdAt), 'MMM dd, yyyy') : 'N/A',
          organizationName: e?.organizationId?.name || 'N/A',
        }));

        dispatch(setGlobalLoader(false));

        resolve({
          data: data,
          page: query.page,
          totalCount: apiData?.total || 0,
        });
      } catch (error) {
        dispatch(setGlobalLoader(false));
        console.log("get expenses error", error);
        toast.error(error?.response?.data?.msg ?? 'Error fetching expenses');
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
      }
    });
  }

  const fetchSummary = async (overrides = {}) => {
    try {
      const sd = overrides.startDate !== undefined ? overrides.startDate : startDate;
      const ed = overrides.endDate !== undefined ? overrides.endDate : endDate;
      const org = overrides.org !== undefined ? overrides.org : selectedOrganization;
      const type = overrides.type !== undefined ? overrides.type : selectedExpenseType;

      const params = new URLSearchParams();
      if (org?.value) params.append('organizationId', org.value);
      if (sd) params.append('startDate', format(sd, 'yyyy-MM-dd'));
      if (ed) params.append('endDate', format(ed, 'yyyy-MM-dd'));
      if (type?.value) params.append('type', type.value);

      const res = await getExpenseSummary(params.toString() ? `?${params.toString()}` : '');
      setSummary(res?.data?.data || null);
    } catch (err) {
      console.log('summary fetch error', err);
    }
  };

  const handleFilter = () => {
    fetchSummary();
    if (tableRef.current) {
      tableRef.current.onQueryChange();
    }
  }

  const handleReset = () => {
    const defaultStart = getDefaultStartDate();
    const defaultEnd = getDefaultEndDate();
    setSelectedOrganization(null);
    setSelectedExpenseType(null);
    setDateRange([{ startDate: defaultStart, endDate: defaultEnd, key: 'selection' }]);
    setShowDatePicker(false);
    fetchSummary({ startDate: defaultStart, endDate: defaultEnd, org: null, type: null });
    setTimeout(() => {
      if (tableRef.current) tableRef.current.onQueryChange();
    }, 100);
  }

  const handleDownload = async () => {
    dispatch(setGlobalLoader(true));
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('limit', '10000');
      if (selectedOrganization?.value) {
        queryParams.append('organizationId', selectedOrganization.value);
      }
      if (startDate) {
        queryParams.append('startDate', format(startDate, 'yyyy-MM-dd'));
      }
      if (endDate) {
        queryParams.append('endDate', format(endDate, 'yyyy-MM-dd'));
      }
      if (selectedExpenseType?.value) {
        queryParams.append('type', selectedExpenseType.value);
      }
      const queryString = `?${queryParams.toString()}`;

      const expenseRes = await getExpenses(queryString);
      const allExpenses = expenseRes?.data?.data?.docs || [];

      if (allExpenses.length === 0) {
        toast.info('No data to export');
        dispatch(setGlobalLoader(false));
        return;
      }

      const rows = allExpenses.map((e, i) => ({
        'Sr No.': i + 1,
        'Amount': e?.amount || '',
        'Category': getExpenseLabel(e?.type) || '',
        'Description': e?.description || '',
        'Date': e?.createdAt ? format(new Date(e.createdAt), 'MMM dd, yyyy') : '',
      }));
      const headers = ['Sr No.', 'Amount', 'Category', 'Description', 'Date'];
      await exportToExcel(rows, headers, 'Expenses');

      dispatch(setGlobalLoader(false));
    } catch (error) {
      dispatch(setGlobalLoader(false));
      console.log('export error', error);
      toast.error('Error exporting expenses');
    }
  }

  useEffect(() => {
    fetchSummary();
    if (profile === PROFILES.ADMIN) {
      async function fetchOrganizations() {
        try {
          const orgRes = await getOrganizations("?limit=10000");
          const data = (orgRes?.data?.data?.docs || []).map((d) => ({
            label: d?.name,
            value: d?._id,
          }))
          setOrganizations(data);
        } catch (error) {
          console.log("get Organizations error", error);
        }
      }
      fetchOrganizations();
    }
  }, [profile]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box>
      <AppBar
        position={'fixed'}
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
        elevation={0}
      >
        <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
          <Topbar onSidebarOpen={handleSidebarOpen} />
        </Container>
      </AppBar>
      <Sidebar
        onClose={handleSidebarClose}
        open={open}
        variant={isMd ? 'permanent' : 'temporary'}
      />
      <main>
        <Box height={{ xs: 94, sm: 102, md: 107 }} />
        <Box
          display="flex"
          flex="1 1 auto"
          overflow="hidden"
          paddingLeft={{ md: '256px' }}
        >
          <Box display="flex" flex="1 1 auto" overflow="hidden">
            <Box flex="1 1 auto" height="100%" overflow="auto">
              {/* Filters Section */}
              <Box px={{ xs: 3, md: 4 }} pt={3}>
                <Typography variant="subtitle2" fontWeight={700} mb={2}>
                  Filters
                </Typography>
                <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                  {/* Date Range Picker */}
                  <Box sx={{ position: 'relative' }}>
                    <TextField
                      size="small"
                      value={
                        startDate && endDate
                          ? `${format(startDate, 'MMM dd, yyyy')} - ${format(endDate, 'MMM dd, yyyy')}`
                          : ''
                      }
                      placeholder="Select date range"
                      onClick={() => setShowDatePicker(!showDatePicker)}
                      InputProps={{
                        readOnly: true,
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarMonthIcon />
                          </InputAdornment>
                        ),
                        endAdornment: startDate && endDate && (
                          <InputAdornment position="end">
                            <ClearIcon
                              sx={{ cursor: 'pointer' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDateRange([
                                  {
                                    startDate: null,
                                    endDate: null,
                                    key: 'selection',
                                  },
                                ]);
                                setShowDatePicker(false);
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ minWidth: 280, cursor: 'pointer' }}
                    />
                    {showDatePicker && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          zIndex: 1000,
                          mt: 1,
                          boxShadow: 3,
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                        }}
                      >
                        <DateRangePicker
                          onChange={handleDateRangeChange}
                          ranges={[
                            {
                              startDate: dateRange[0].startDate || new Date(),
                              endDate: dateRange[0].endDate || new Date(),
                              key: 'selection',
                            },
                          ]}
                          minDate={minDateObj}
                          maxDate={today}
                          rangeColors={[theme.palette.primary.main]}
                          locale={enUS}
                        />
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            p: 1,
                            borderTop: `1px solid ${theme.palette.divider}`,
                          }}
                        >
                          <Button
                            size="small"
                            onClick={() => setShowDatePicker(false)}
                          >
                            Close
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {/* Expense Type Filter */}
                  <Select
                    styles={{
                      menu: provided => ({ ...provided, zIndex: 9999 }),
                      control: provided => ({ ...provided, minHeight: 40, minWidth: 200 })
                    }}
                    placeholder="Expense Type"
                    value={selectedExpenseType}
                    options={EXPENSE_TYPES}
                    isClearable
                    onChange={(e) => setSelectedExpenseType(e)}
                  />

                  {/* Organization Filter - Admin Only */}
                  {profile === PROFILES.ADMIN && (
                    <Select
                      styles={{
                        menu: provided => ({ ...provided, zIndex: 9999 }),
                        control: provided => ({ ...provided, minHeight: 40, minWidth: 250 })
                      }}
                      placeholder="Select Organization"
                      value={selectedOrganization}
                      options={organizations}
                      isClearable
                      onChange={(e) => setSelectedOrganization(e)}
                    />
                  )}

                  {/* Filter, Reset & Download Buttons */}
                  <Button
                    variant="outlined"
                    color="primary"
                    size="medium"
                    onClick={handleFilter}
                  >
                    Filter
                  </Button>
                  <Button
                    variant="text"
                    color="error"
                    size="medium"
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="medium"
                    onClick={handleDownload}
                  >
                    Download
                  </Button>
                </Box>
              </Box>

              {/* Summary Cards */}
              {summary && (
                <Box px={{ xs: 3, md: 4 }} pt={3}>
                  <Box display="flex" gap={2} flexWrap="wrap">
                    {/* Total Spend */}
                    <Box sx={{
                      display: 'inline-flex', flexDirection: 'column', borderRadius: 2, p: 2,
                      background: `linear-gradient(135deg, ${alpha('#1565C0', 0.08)} 0%, ${alpha('#1565C0', 0.03)} 100%)`,
                      border: `1px solid ${alpha('#1565C0', 0.18)}`,
                    }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.5}>
                        TOTAL SPEND
                      </Typography>
                      <Typography variant="h5" fontWeight={800} color="primary.main" mt={0.5}>
                        ₹{summary.totalAmount.toLocaleString('en-IN')}
                      </Typography>
                    </Box>

                    {/* Per category breakdown */}
                    {summary.byCategory.length > 0 && (
                      <Box sx={{
                        display: 'inline-flex', flexDirection: 'column', borderRadius: 2, p: 2,
                        background: `linear-gradient(135deg, ${alpha('#E65100', 0.07)} 0%, ${alpha('#E65100', 0.02)} 100%)`,
                        border: `1px solid ${alpha('#E65100', 0.18)}`,
                      }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.5} display="block" mb={1}>
                          BY CATEGORY
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {summary.byCategory.map(cat => (
                            <Box key={cat._id} sx={{
                              display: 'flex', alignItems: 'center', gap: 0.75,
                              bgcolor: alpha('#E65100', 0.08), borderRadius: 1, px: 1, py: 0.4,
                            }}>
                              <Typography variant="caption" fontWeight={700} sx={{ color: '#E65100', textTransform: 'capitalize' }}>
                                {getExpenseLabel(cat._id)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ₹{cat.total.toLocaleString('en-IN')}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {/* Add Expense Button - Non-Admin Only */}
              {profile !== PROFILES.ADMIN && (
                <Box marginTop={3}
                  marginLeft={{ xs: 3, md: 4 }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    component="button"
                    size="large"
                    onClick={() => {
                      navigate('/expenses/add');
                    }}
                  >
                    Add Expense
                  </Button>
                </Box>
              )}
              <TableComponent ref={tableRef} data={fetchExpenses} columns={columns} title='Expenses' isRemoteData={true} />
              <Divider />
            </Box>
          </Box>
        </Box>
      </main>
    </Box>
  );
};

export default Expenses;
